import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import DashboardLayout from '@/components/layout/DashboardLayout';

interface PositRow {
  id: string;
  title: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  is_public?: boolean | null;
}

export default function PositsTable() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-posits', page, search],
    queryFn: async () => {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from('posits')
        .select('id, title, user_id, created_at, updated_at, is_public', { count: 'exact' })
        .order('updated_at', { ascending: false })
        .range(from, to);

      if (search) {
        query = query.ilike('title', `%${search}%`);
      }

      const { data: rows, count } = await query as any;
      return { rows: (rows || []) as PositRow[], count: count || 0 };
    },
  });

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data?.count || 0) / pageSize)), [data?.count]);

  return (
    <DashboardLayout title="Posits" maxWidth="7xl">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2 w-full justify-end">
          <Input placeholder="Search title…" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="w-64" />
        </div>
      </div>

        <div className="border rounded-lg">
          {isLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Public</TableHead>
                <TableHead>Updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows || []).map((p) => (
                <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/admin/business/posits/${p.id}`)}>
                  <TableCell className="font-medium">{p.title}</TableCell>
                  <TableCell className="font-mono text-xs">{p.user_id}</TableCell>
                  <TableCell>{p.is_public ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{new Date(p.updated_at).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {(!isLoading && (data?.rows || []).length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">No posits found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
            <Button variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        </div>
    </DashboardLayout>
  );
}


